import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { WelcomeTour } from "@/components/WelcomeTour";
import { DailyQuote } from "@/components/DailyQuote";
import { QuotePanel } from "@/components/QuotePanel";
import { TrialEndingNotice } from "@/components/TrialEndingNotice";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine } from "recharts";
import { MessageCircleHeart, HeartPulse, Flame, TreeDeciduous, Sparkles, ArrowRight, Target, HelpCircle } from "lucide-react";

function StatCard({ icon: Icon, label, value, tint, delay }) {
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay }}
                className="ambient-shadow rounded-3xl border border-border/60 bg-card p-6">
      <div className={`flex h-10 w-10 items-center justify-center rounded-2xl ${tint}`}>
        <Icon className="h-5 w-5" strokeWidth={1.5} />
      </div>
      <div className="mt-4 font-serif text-3xl">{value}</div>
      <div className="text-sm text-muted-foreground">{label}</div>
    </motion.div>
  );
}

export default function Dashboard() {
  const { user, setUser } = useAuth();
  const { t } = useI18n();
  const [checkins, setCheckins] = useState([]);
  const [game, setGame] = useState({});
  const [goals, setGoals] = useState([]);
  const [welcome, setWelcome] = useState(false);
  const [selIdx, setSelIdx] = useState(null);

  useEffect(() => {
    api.get("/checkins").then(({ data }) => setCheckins(data.checkins || [])).catch(() => {});
    api.get("/gamification").then(({ data }) => setGame(data || {})).catch(() => {});
    api.get("/goals").then(({ data }) => setGoals(data.goals || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (user && user.tour_completed === false) setWelcome(true);
  }, [user]);

  const closeWelcome = () => {
    setWelcome(false);
    setUser?.((u) => (u ? { ...u, tour_completed: true } : u));
    api.post("/tour/complete").catch(() => {});
  };

  const chartData = [...checkins].reverse().slice(-14).map((c, i) => ({
    i,
    date: new Date(c.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    fullDate: new Date(c.created_at).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" }),
    mood: c.mood,
    energy: c.energy,
    sleep: c.sleep,
    stress: c.stress,
    anxiety: c.anxiety,
    confidence: c.confidence,
    note: c.note,
    answer: c.answer,
    question: c.question,
  }));
  const effIdx = selIdx != null && selIdx < chartData.length ? selIdx : chartData.length - 1;
  const selected = chartData[effIdx];
  const makeDot = (color) => (props) => {
    const { cx, cy, index } = props;
    if (cx == null || cy == null) return null;
    const active = index === effIdx;
    return (
      <circle key={`${color}-${index}`} cx={cx} cy={cy} r={active ? 6 : 3}
              fill={active ? color : "hsl(var(--card))"} stroke={color} strokeWidth={active ? 2.5 : 1.5}
              style={{ cursor: "pointer", transition: "r 0.15s ease" }} />
    );
  };
  const latest = checkins[0];
  const hour = new Date().getHours();

  const metricTint = (key, val) => {
    if (val == null) return "bg-secondary text-muted-foreground";
    const negative = key === "stress" || key === "anxiety";
    const good = negative ? val <= 3 : val >= 7;
    const bad = negative ? val >= 7 : val <= 3;
    if (good) return "bg-emerald-500/10 text-emerald-600";
    if (bad) return "bg-rose-500/10 text-rose-600";
    return "bg-amber-500/10 text-amber-600";
  };
  const greeting = hour < 12 ? t("dash.morning") : hour < 18 ? t("dash.afternoon") : t("dash.evening");

  return (
    <div>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm text-muted-foreground">{greeting}</p>
            <h1 className="font-serif text-3xl font-semibold tracking-tight sm:text-4xl">{t("dash.hi")} {user?.name?.split(" ")[0] || "there"} 🌿</h1>
            <p className="mt-2 max-w-lg text-muted-foreground">{t("dash.subtitle")}</p>
          </div>
          <Link to="/app/guide" className="hidden sm:block">
            <Button variant="outline" size="sm" className="rounded-full gap-2" data-testid="how-it-works-button">
              <HelpCircle className="h-4 w-4" strokeWidth={1.5} /> {t("common.howItWorks", "How it works")}
            </Button>
          </Link>
        </div>
      </motion.div>

      <TrialEndingNotice />

      <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon={HeartPulse} label={t("dash.latestMood")} value={latest ? `${latest.mood}/10` : "—"} tint="bg-primary/10 text-primary" delay={0.05} />
        <StatCard icon={Flame} label={t("dash.dayStreak")} value={game.streak ?? 0} tint="bg-orange-500/10 text-orange-500" delay={0.1} />
        <StatCard icon={TreeDeciduous} label={t("dash.growthPoints")} value={game.growth_points ?? 0} tint="bg-sage/10 text-sage" delay={0.15} />
        <StatCard icon={Target} label={t("dash.activeGoals")} value={goals.filter((g) => g.status === "active").length} tint="bg-accent-foreground/10 text-accent-foreground" delay={0.2} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
                    className="ambient-shadow rounded-3xl border border-border/60 bg-card p-6 lg:col-span-2">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-serif text-xl">{t("dash.moodEnergy")}</h2>
              {chartData.length > 1 && <p className="mt-0.5 text-xs text-muted-foreground">{t("dash.tapHint")}</p>}
            </div>
            <Link to="/app/insights"><Button variant="ghost" size="sm" className="rounded-full gap-1 text-primary" data-testid="view-insights-link">{t("dash.insights")} <ArrowRight className="h-3.5 w-3.5" /></Button></Link>
          </div>
          {chartData.length > 1 ? (
            <>
            <div className="mt-4 h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                           onClick={(e) => { if (e && e.activeTooltipIndex != null) setSelIdx(e.activeTooltipIndex); }}
                           style={{ cursor: "pointer" }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                  <YAxis domain={[0, 10]} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 12 }} />
                  {selected && <ReferenceLine x={selected.date} stroke="hsl(var(--muted-foreground))" strokeDasharray="4 4" strokeOpacity={0.6} />}
                  <Line type="monotone" dataKey="mood" stroke="hsl(var(--primary))" strokeWidth={2.5} dot={makeDot("hsl(var(--primary))")} activeDot={{ r: 6 }} />
                  <Line type="monotone" dataKey="energy" stroke="hsl(var(--sage))" strokeWidth={2.5} dot={makeDot("hsl(var(--sage))")} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            {selected && (
              <div className="mt-4 rounded-2xl border border-border/50 bg-secondary/40 p-4" data-testid="checkin-day-detail">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-medium capitalize" data-testid="detail-date">{selected.fullDate}</p>
                  <div className="flex flex-wrap gap-2">
                    <span className={`rounded-full px-3 py-1 text-xs font-medium ${metricTint("mood", selected.mood)}`} data-testid="detail-mood-pill">{t("dash.moodPill")} {selected.mood}/10</span>
                    <span className={`rounded-full px-3 py-1 text-xs font-medium ${metricTint("energy", selected.energy)}`} data-testid="detail-energy-pill">{t("dash.energyPill")} {selected.energy}/10</span>
                    {[
                      { key: "sleep", label: t("checkin.sleep"), val: selected.sleep },
                      { key: "stress", label: t("checkin.stress"), val: selected.stress },
                      { key: "anxiety", label: t("checkin.anxiety"), val: selected.anxiety },
                      { key: "confidence", label: t("checkin.confidence"), val: selected.confidence },
                    ].filter((m) => m.val != null).map((m) => (
                      <span key={m.key} className={`rounded-full px-3 py-1 text-xs font-medium ${metricTint(m.key, m.val)}`} data-testid={`detail-${m.key}-pill`}>{m.label} {m.val}/10</span>
                    ))}
                  </div>
                </div>
                <div className="mt-3 text-sm text-muted-foreground" data-testid="detail-reflection">
                  {(selected.note || selected.answer) ? (
                    <>
                      {selected.question && !selected.note && (
                        <span className="mb-1 block text-xs italic text-muted-foreground/70">{selected.question}</span>
                      )}
                      {selected.note || selected.answer}
                    </>
                  ) : t("dash.noReflection")}
                </div>
              </div>
            )}
            </>
          ) : (
            <p className="mt-8 text-sm text-muted-foreground">{t("dash.emptyChart")}</p>
          )}
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.25 }}
                    className="ambient-shadow flex flex-col justify-between rounded-3xl border border-border/60 bg-gradient-to-b from-primary/10 to-card p-6">
          <div>
            <MessageCircleHeart className="h-8 w-8 text-primary" strokeWidth={1.5} />
            <h2 className="mt-4 font-serif text-xl">{t("dash.talkTitle")}</h2>
            <p className="mt-2 text-sm text-muted-foreground">{t("dash.talkSub")}</p>
          </div>
          <div className="mt-6 space-y-2">
            <Link to="/app/companion"><Button className="w-full rounded-full" data-testid="open-companion-button">{t("dash.openCompanion")}</Button></Link>
            <Link to="/app/checkin"><Button variant="outline" className="w-full rounded-full gap-2" data-testid="quick-checkin-button"><Sparkles className="h-4 w-4" strokeWidth={1.5} /> {t("dash.dailyCheckin")}</Button></Link>
          </div>
        </motion.div>
      </div>

      <div className="mt-6">
        <QuotePanel variant="card" />
      </div>

      <WelcomeTour open={welcome} onComplete={closeWelcome} />
      {!welcome && <DailyQuote />}
    </div>
  );
}
